import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  SimpleGrid,
  Select,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  FiPlus,
  FiTrash2,
  FiRefreshCw,
  FiArrowLeft,
  FiMessageSquare,
  FiChevronRight,
} from "react-icons/fi";
import { useDispatch } from "react-redux";
import { hitBeApi } from "../../../api/api";
import { appStore } from "../../../store/Store";
import VoiceCreditPaymentModal from "../VoiceCreditPaymentModal";
import {
  AiCreditsBanner,
  AiCreditsPaywall,
  AiFeatureGuidePanel,
} from "../ai/aiFeatureUi";
import { useAiCreditsGate } from "../../../hooks/useAiCreditsGate";
import { DASHBOARD_TAB_GUIDES } from "../../../content/productGuide";
import ExamFolderCard from "./ExamFolderCard";
import ExamChatPanel from "./ExamChatPanel";
import CurrentAffairs from "./CurrentAffairs";

const GUIDE = DASHBOARD_TAB_GUIDES["exam-ai"];

export default function ExamAi() {
  const toast = useToast();
  const dispatch = useDispatch();

  const {
    remaining: aiCreditsRemaining,
    canUse: canUseAi,
    payOpen,
    setPayOpen,
    promptBuy: promptBuyCredits,
  } = useAiCreditsGate();

  const [view, setView] = useState("syllabus"); // "syllabus" | "current-affairs"
  const [exams, setExams] = useState([]);
  const [examsLoading, setExamsLoading] = useState(true);
  const [activeExamId, setActiveExamId] = useState(null);
  const [newExamName, setNewExamName] = useState("");
  const [examCreating, setExamCreating] = useState(false);

  const [tree, setTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deletingCats, setDeletingCats] = useState(false);

  /** Stack of category ids we've drilled into ([] = root subjects grid). */
  const [drillIds, setDrillIds] = useState([]);
  const [generatingId, setGeneratingId] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const [sending, setSending] = useState(false);

  const pageBg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");

  const applyCredits = useCallback(
    (userTokenDetails) => {
      if (userTokenDetails) {
        const currentUser = appStore.getState().user || {};
        dispatch({
          type: "SET_USER",
          value: { ...currentUser, userTokenDetails },
        });
      }
    },
    [dispatch]
  );

  const showError = useCallback(
    (error, fallback) => {
      toast({
        title: fallback,
        description: String(error?.message || "").slice(0, 200) || undefined,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    },
    [toast]
  );

  const loadExams = useCallback(async () => {
    setExamsLoading(true);
    try {
      const res = await hitBeApi("dashboard/exam-ai/exams", "GET");
      const list = res?.data?.exams || [];
      setExams(list);
      setActiveExamId((prev) => prev || (list[0]?._id ?? null));
    } catch (error) {
      showError(error, "Could not load exams.");
    } finally {
      setExamsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  const loadTree = useCallback(
    async (examId) => {
      if (!examId) {
        setTree([]);
        return;
      }
      setTreeLoading(true);
      try {
        const res = await hitBeApi(`dashboard/exam-ai/exams/${examId}`, "GET");
        setTree(res?.data?.tree || []);
      } catch (error) {
        showError(error, "Could not load syllabus.");
      } finally {
        setTreeLoading(false);
      }
    },
    [showError]
  );

  // Switching exam swaps the whole tree and clears drill/chat selection.
  useEffect(() => {
    setSelectedIds(new Set());
    setDrillIds([]);
    setActiveCategory(null);
    setChats([]);
    setActiveChat(null);
    loadTree(activeExamId);
  }, [activeExamId, loadTree]);

  const handleCreateExam = async () => {
    const name = newExamName.trim();
    if (!name) return;
    if (!canUseAi) {
      promptBuyCredits();
      return;
    }
    setExamCreating(true);
    try {
      const res = await hitBeApi("dashboard/exam-ai/exams", "POST", { name });
      applyCredits(res?.data?.userTokenDetails);
      setNewExamName("");
      const created = res?.data?.exam;
      await loadExams();
      if (created?._id) {
        setActiveExamId(created._id);
        setTree(res?.data?.tree || []);
      }
      toast({
        title: `${created?.name || name} added`,
        description: "Syllabus and topics created.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      showError(error, "Could not create exam.");
    } finally {
      setExamCreating(false);
    }
  };

  const handleDeleteExam = async () => {
    if (!activeExamId) return;
    const exam = exams.find((e) => String(e._id) === String(activeExamId));
    if (!window.confirm(`Delete "${exam?.name || "this exam"}" and its syllabus?`)) {
      return;
    }
    try {
      await hitBeApi(`dashboard/exam-ai/exams/${activeExamId}`, "DELETE");
      const remaining = exams.filter((e) => String(e._id) !== String(activeExamId));
      setExams(remaining);
      setActiveExamId(remaining[0]?._id ?? null);
    } catch (error) {
      showError(error, "Could not delete exam.");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setDeletingCats(true);
    try {
      await hitBeApi("dashboard/exam-ai/categories", "DELETE", { ids });
      setSelectedIds(new Set());
      // If the open chat's category or drilled subject was removed, reset.
      if (activeCategory && ids.includes(String(activeCategory._id))) {
        setActiveCategory(null);
        setChats([]);
        setActiveChat(null);
      }
      setDrillIds((prev) => prev.filter((id) => !ids.includes(String(id))));
      await loadTree(activeExamId);
    } catch (error) {
      showError(error, "Could not delete categories.");
    } finally {
      setDeletingCats(false);
    }
  };

  const handleDeleteOne = async (node) => {
    const label = node.level === "topic" ? "topic" : "subject (and its topics)";
    if (!window.confirm(`Delete this ${label}?`)) return;
    try {
      await hitBeApi("dashboard/exam-ai/categories", "DELETE", {
        ids: [String(node._id)],
      });
      if (activeCategory && String(activeCategory._id) === String(node._id)) {
        setActiveCategory(null);
        setChats([]);
        setActiveChat(null);
      }
      setDrillIds((prev) => prev.filter((id) => String(id) !== String(node._id)));
      await loadTree(activeExamId);
    } catch (error) {
      showError(error, "Could not delete.");
    }
  };

  const handleGenerateSubtopics = async (categoryId) => {
    if (!canUseAi) {
      promptBuyCredits();
      return;
    }
    setGeneratingId(String(categoryId));
    try {
      const res = await hitBeApi(
        `dashboard/exam-ai/categories/${categoryId}/generate-subtopics`,
        "POST"
      );
      applyCredits(res?.data?.userTokenDetails);
      if (res?.data?.tree) setTree(res.data.tree);
      toast({
        title: `Added ${res?.data?.added || 0} sub-topics`,
        description: (res?.data?.names || []).slice(0, 4).join(", ") || undefined,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      showError(error, "Could not generate sub-topics.");
    } finally {
      setGeneratingId(null);
    }
  };

  const loadChats = useCallback(
    async (categoryId) => {
      setChatsLoading(true);
      try {
        const res = await hitBeApi(
          `dashboard/exam-ai/categories/${categoryId}/chats`,
          "GET"
        );
        const list = res?.data?.chats || [];
        setChats(list);
        return list;
      } catch (error) {
        showError(error, "Could not load chats.");
        setChats([]);
        return [];
      } finally {
        setChatsLoading(false);
      }
    },
    [showError]
  );

  const openChat = useCallback(
    async (chatId) => {
      try {
        const res = await hitBeApi(`dashboard/exam-ai/chats/${chatId}`, "GET");
        setActiveChat(res?.data?.chat || null);
      } catch (error) {
        showError(error, "Could not open chat.");
      }
    },
    [showError]
  );

  const handleSelectCategory = async (node) => {
    setActiveCategory(node);
    setActiveChat(null);
    const list = await loadChats(node._id);
    // Auto-open the most recent past chat so previous Q&A is visible right away.
    if (list.length) {
      await openChat(list[0]._id);
    }
  };

  const handleNewChat = async () => {
    if (!activeCategory) return;
    try {
      const res = await hitBeApi("dashboard/exam-ai/chats", "POST", {
        categoryId: activeCategory._id,
      });
      const chat = res?.data?.chat;
      if (chat) {
        setActiveChat(chat);
        setChats((prev) => [
          { ...chat, messageCount: 0 },
          ...prev.filter((c) => String(c._id) !== String(chat._id)),
        ]);
      }
    } catch (error) {
      showError(error, "Could not start chat.");
    }
  };

  const handleSend = async (text) => {
    if (!activeChat) return;
    if (!canUseAi) {
      promptBuyCredits();
      return;
    }
    setSending(true);
    try {
      const res = await hitBeApi(
        `dashboard/exam-ai/chats/${activeChat._id}/messages`,
        "POST",
        { question: text }
      );
      applyCredits(res?.data?.userTokenDetails);
      const updated = res?.data?.chat;
      if (updated) {
        setActiveChat(updated);
        setChats((prev) =>
          prev.map((c) =>
            String(c._id) === String(updated._id)
              ? { ...c, title: updated.title, messageCount: updated.messages.length }
              : c
          )
        );
      }
    } catch (error) {
      showError(error, "Could not get an answer.");
    } finally {
      setSending(false);
    }
  };

  const handleMoveChat = async (categoryId) => {
    if (!activeChat || !categoryId) return;
    try {
      const res = await hitBeApi(
        `dashboard/exam-ai/chats/${activeChat._id}`,
        "PATCH",
        { categoryId }
      );
      const updated = res?.data?.chat;
      if (updated) {
        setActiveChat(updated);
        // Moved out of the currently open category — drop it from this list.
        if (
          activeCategory &&
          String(categoryId) !== String(activeCategory._id)
        ) {
          setChats((prev) =>
            prev.filter((c) => String(c._id) !== String(updated._id))
          );
          setActiveChat(null);
        }
        toast({
          title: "Chat moved",
          status: "success",
          duration: 2500,
          isClosable: true,
        });
      }
    } catch (error) {
      showError(error, "Could not move chat.");
    }
  };

  const activeExam = exams.find((e) => String(e._id) === String(activeExamId));

  // Resolve the drill path against the (possibly reloaded) tree.
  const drillNodes = [];
  let drillLevel = tree;
  for (const id of drillIds) {
    const found = drillLevel.find((n) => String(n._id) === String(id));
    if (!found) break;
    drillNodes.push(found);
    drillLevel = found.children || [];
  }
  const currentNode = drillNodes.length ? drillNodes[drillNodes.length - 1] : null;
  const currentChildren = currentNode ? currentNode.children || [] : tree;

  return (
    <Box bg={pageBg} minH="100%" py={2}>
      <AiCreditsBanner
        canUse={canUseAi}
        remaining={aiCreditsRemaining}
        onBuy={promptBuyCredits}
      />

      <HStack spacing={2} mb={4}>
        <Button
          size="sm"
          colorScheme="purple"
          variant={view === "syllabus" ? "solid" : "outline"}
          onClick={() => setView("syllabus")}
        >
          Syllabus
        </Button>
        <Button
          size="sm"
          colorScheme="purple"
          variant={view === "current-affairs" ? "solid" : "outline"}
          onClick={() => setView("current-affairs")}
        >
          Current Affairs
        </Button>
      </HStack>

      {view === "current-affairs" ? (
        <CurrentAffairs
          canUseAi={canUseAi}
          promptBuyCredits={promptBuyCredits}
          applyCredits={applyCredits}
        />
      ) : (
        <>
          {/* Top toolbar: exam selector + add exam */}
          <Box
            mb={4}
            bg={cardBg}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="md"
            overflow="hidden"
          >
            <Box p={4} bgGradient="linear(to-r, purple.500, pink.500)" color="white">
              <HStack justify="space-between" mb={3}>
                <Heading size="sm">Exam AI · Syllabus</Heading>
                <IconButton
                  size="sm"
                  variant="ghost"
                  color="white"
                  aria-label="Refresh"
                  icon={<FiRefreshCw />}
                  onClick={() => loadTree(activeExamId)}
                  _hover={{ bg: "whiteAlpha.200" }}
                />
              </HStack>
              <Flex gap={2} direction={{ base: "column", md: "row" }}>
                <HStack spacing={2} flex="1">
                  <Select
                    size="sm"
                    bg="whiteAlpha.300"
                    color="white"
                    borderColor="whiteAlpha.400"
                    sx={{ option: { color: "black" } }}
                    placeholder={examsLoading ? "Loading…" : "Select exam"}
                    value={activeExamId || ""}
                    onChange={(e) => setActiveExamId(e.target.value || null)}
                  >
                    {exams.map((exam) => (
                      <option key={String(exam._id)} value={String(exam._id)}>
                        {exam.name}
                        {exam.status === "building" ? " (building…)" : ""}
                      </option>
                    ))}
                  </Select>
                  {activeExamId ? (
                    <IconButton
                      size="sm"
                      variant="ghost"
                      color="white"
                      aria-label="Delete exam"
                      icon={<FiTrash2 />}
                      onClick={handleDeleteExam}
                      _hover={{ bg: "whiteAlpha.200" }}
                    />
                  ) : null}
                </HStack>
                <HStack spacing={2} flex="1">
                  <Input
                    size="sm"
                    bg="whiteAlpha.300"
                    color="white"
                    borderColor="whiteAlpha.400"
                    _placeholder={{ color: "whiteAlpha.700" }}
                    placeholder="Add exam (e.g. UPSC)"
                    value={newExamName}
                    onChange={(e) => setNewExamName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateExam();
                    }}
                  />
                  <IconButton
                    size="sm"
                    colorScheme="whiteAlpha"
                    aria-label="Add exam"
                    icon={<FiPlus />}
                    isLoading={examCreating}
                    onClick={handleCreateExam}
                  />
                </HStack>
              </Flex>
            </Box>
          </Box>

          {GUIDE && !activeCategory && !currentNode ? (
            <AiFeatureGuidePanel
              title={GUIDE.title}
              intro={GUIDE.intro}
              helps={GUIDE.helps}
              theme="purple"
            />
          ) : null}

          {/* Content card */}
          <Box
            position="relative"
            bg={cardBg}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="lg"
            overflow="hidden"
            minH={{ base: "50vh", lg: "calc(100vh - 13rem)" }}
            display="flex"
            flexDirection="column"
          >
            <AiCreditsPaywall
              show={!canUseAi}
              featureName="Exam AI"
              onBuy={promptBuyCredits}
              theme="purple"
            />

            {examCreating ? (
              <Flex flex="1" direction="column" align="center" justify="center" gap={3} p={8}>
                <Spinner color="purple.500" />
                <Text color={muted} fontSize="sm">
                  Building syllabus &amp; topics…
                </Text>
              </Flex>
            ) : !exams.length ? (
              <Flex flex="1" align="center" justify="center" p={8}>
                <Text color={muted} fontSize="sm" textAlign="center" lineHeight="tall">
                  Add your first exam above to auto-build its syllabus, then open a
                  subject or topic to study.
                </Text>
              </Flex>
            ) : treeLoading ? (
              <Flex flex="1" align="center" justify="center" py={12}>
                <Spinner color="purple.500" />
              </Flex>
            ) : activeExam?.status === "building" ? (
              <Flex flex="1" align="center" justify="center" p={8}>
                <Text color={muted} fontSize="sm" textAlign="center">
                  This exam is still building its syllabus. Try refresh in a moment.
                </Text>
              </Flex>
            ) : activeCategory ? (
              /* ---- Chat view ---- */
              <Flex direction="column" flex="1" minH={0}>
                <Box px={4} pt={4} pb={2} borderBottomWidth="1px" borderColor={borderColor}>
                  <HStack mb={2} spacing={1} color={muted} fontSize="xs" flexWrap="wrap">
                    <Button
                      size="xs"
                      variant="ghost"
                      leftIcon={<FiArrowLeft />}
                      onClick={() => {
                        setActiveCategory(null);
                        setActiveChat(null);
                      }}
                    >
                      Back
                    </Button>
                    <Text noOfLines={1}>
                      {[activeExam?.name, ...drillNodes.map((n) => n.name)]
                        .filter(Boolean)
                        .join("  ›  ")}
                    </Text>
                  </HStack>
                  <HStack justify="space-between" align="center" mt={1}>
                    <Box minW={0}>
                      <Text
                        fontSize="xs"
                        color={muted}
                        textTransform="uppercase"
                        letterSpacing="wide"
                      >
                        {activeCategory.level === "topic" ? "Topic" : "Subject"}
                      </Text>
                      <Heading size="sm" noOfLines={1}>
                        {activeCategory.name}
                      </Heading>
                    </Box>
                    <Button
                      size="xs"
                      colorScheme="purple"
                      leftIcon={<FiPlus />}
                      onClick={handleNewChat}
                    >
                      New chat
                    </Button>
                  </HStack>

                  {chatsLoading ? (
                    <Spinner size="xs" color="purple.500" mt={2} />
                  ) : chats.length ? (
                    <HStack spacing={2} mt={2} overflowX="auto" pb={1}>
                      {chats.map((c) => (
                        <Button
                          key={String(c._id)}
                          size="xs"
                          flexShrink={0}
                          variant={
                            String(activeChat?._id) === String(c._id) ? "solid" : "outline"
                          }
                          colorScheme="purple"
                          onClick={() => openChat(c._id)}
                        >
                          {c.title || "Chat"}
                        </Button>
                      ))}
                    </HStack>
                  ) : (
                    <Text fontSize="xs" color={muted} mt={2}>
                      No chats yet — start one to ask questions.
                    </Text>
                  )}
                </Box>

                <ExamChatPanel
                  chat={activeChat}
                  tree={tree}
                  sending={sending}
                  onSend={handleSend}
                  onMoveCategory={handleMoveChat}
                  canChat={canUseAi}
                  onBuyCredits={promptBuyCredits}
                />
              </Flex>
            ) : (
              /* ---- Folder grid view (Portfolio-style, any depth) ---- */
              <Box p={{ base: 4, md: 5 }} flex="1" overflowY="auto">
                {/* Breadcrumb */}
                <HStack mb={4} spacing={1} fontSize="sm" fontWeight="semibold" flexWrap="wrap">
                  <Button
                    size="sm"
                    variant={currentNode ? "ghost" : "solid"}
                    colorScheme="teal"
                    onClick={() => setDrillIds([])}
                  >
                    All subjects
                  </Button>
                  {drillNodes.map((node, i) => (
                    <HStack key={String(node._id)} spacing={1}>
                      <Box as={FiChevronRight} color={muted} />
                      <Button
                        size="sm"
                        variant={i === drillNodes.length - 1 ? "solid" : "ghost"}
                        colorScheme="blue"
                        onClick={() =>
                          setDrillIds(drillNodes.slice(0, i + 1).map((n) => n._id))
                        }
                      >
                        {node.name}
                      </Button>
                    </HStack>
                  ))}
                </HStack>

                {/* Current-node actions: chat + AI-generate sub-topics */}
                {currentNode ? (
                  <HStack mb={4} spacing={2} flexWrap="wrap">
                    <Button
                      size="sm"
                      colorScheme="teal"
                      variant="outline"
                      leftIcon={<FiMessageSquare />}
                      onClick={() =>
                        handleSelectCategory({
                          _id: currentNode._id,
                          name: currentNode.name,
                          level: currentNode.level,
                        })
                      }
                    >
                      Study “{currentNode.name}” with AI
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      leftIcon={<FiPlus />}
                      isLoading={generatingId === String(currentNode._id)}
                      loadingText="Generating…"
                      onClick={() => handleGenerateSubtopics(currentNode._id)}
                    >
                      Generate sub-topics with AI
                    </Button>
                  </HStack>
                ) : null}

                {/* Multi-select delete bar */}
                {selectedIds.size > 0 ? (
                  <HStack mb={3} spacing={2}>
                    <Button
                      size="sm"
                      colorScheme="red"
                      leftIcon={<FiTrash2 />}
                      isLoading={deletingCats}
                      onClick={handleDeleteSelected}
                    >
                      Delete {selectedIds.size} selected
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                      Clear
                    </Button>
                  </HStack>
                ) : null}

                {currentChildren.length ? (
                  <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4} alignItems="stretch">
                    {currentChildren.map((node) => (
                      <ExamFolderCard
                        key={String(node._id)}
                        name={node.name}
                        variant={currentNode ? "blue" : "teal"}
                        isTopic={!!currentNode}
                        statLabel="Sub-topics"
                        statValue={`${node.children?.length || 0}`}
                        selected={selectedIds.has(String(node._id))}
                        onToggleSelect={() => toggleSelect(String(node._id))}
                        onOpen={() => setDrillIds([...drillIds, node._id])}
                        onChat={() =>
                          handleSelectCategory({
                            _id: node._id,
                            name: node.name,
                            level: node.level,
                          })
                        }
                        onDelete={() =>
                          handleDeleteOne({ _id: node._id, level: node.level })
                        }
                      />
                    ))}
                  </SimpleGrid>
                ) : (
                  <Box textAlign="center" py={10}>
                    <Text color={muted} fontSize="sm" mb={3}>
                      {currentNode
                        ? "No sub-topics here yet."
                        : "No subjects yet for this exam."}
                    </Text>
                    {currentNode ? (
                      <Button
                        size="sm"
                        colorScheme="purple"
                        leftIcon={<FiPlus />}
                        isLoading={generatingId === String(currentNode._id)}
                        loadingText="Generating…"
                        onClick={() => handleGenerateSubtopics(currentNode._id)}
                      >
                        Generate sub-topics with AI
                      </Button>
                    ) : null}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </>
      )}

      <VoiceCreditPaymentModal
        isOpen={payOpen}
        onClose={() => setPayOpen(false)}
        variant={canUseAi ? "topUp" : "exhausted"}
      />
    </Box>
  );
}
